import Axios from "axios";

/* ───────────────────────── Base URL ─────────────────────────
   Dev hits Django directly; Prod uses same-origin (relative). */
const BASE =
  process.env.NODE_ENV === "development" ? "http://127.0.0.1:8000" : "";

/* ───────────────────────── Clients ──────────────────────────
   - api:     authenticated client (JWT, refresh, interceptors)
   - publicApi: unauthenticated client (no auth header, no cookies)
*/
const api = Axios.create({ baseURL: BASE, withCredentials: true });
const publicApi = Axios.create({ baseURL: BASE, withCredentials: false });

/* ─────────────────────── Auth helpers ─────────────────────── */
let accessToken = localStorage.getItem("token") || null;
let refreshToken = localStorage.getItem("refresh") || null;

export function setAuthToken(token) {
  accessToken = token || null;
  if (token) localStorage.setItem("token", token);
  else localStorage.removeItem("token");
}
export function setRefreshToken(token) {
  refreshToken = token || null;
  if (token) localStorage.setItem("refresh", token);
  else localStorage.removeItem("refresh");
}
export function getAccessToken() {
  return accessToken || localStorage.getItem("token");
}
export function getRefreshToken() {
  return refreshToken || localStorage.getItem("refresh");
}
export function clearAuth() {
  setAuthToken(null);
  setRefreshToken(null);
  localStorage.removeItem("roleOverride");
}
export function authHeaders() {
  const t = getAccessToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

/* ───────────── Don’t send auth on these endpoints ───────────
   (Even with the private client; stale tokens can cause 401s.) */
const PUBLIC_PATHS = [
  "/api/username-availability/",
  "/api/pending-users/",        // public registration create
  "/api/token/",                // login
  "/api/token/refresh/",        // refresh
];
const isPublicPath = (url = "") =>
  PUBLIC_PATHS.some((p) => url.endsWith(p) || url.includes(`${p}?`));

/* ───────────────── Request interceptor (api) ─────────────────
   Attach JWT to same-origin, non-public requests only. */
api.interceptors.request.use((config) => {
  const url = String(config.url || "");
  // never attach to absolute cross-origin URLs
  const isAbsolute = /^https?:\/\//i.test(url);
  if (!isAbsolute && !isPublicPath(url)) {
    const t = getAccessToken();
    if (t && !config.headers?.Authorization) {
      config.headers = { ...(config.headers || {}), Authorization: `Bearer ${t}` };
    }
  }
  return config;
});

/* ───────────────── Refresh on 401/403 (api) ───────────────── */
let isRefreshing = false;
let waiters = [];
const notify = (tok) => { waiters.forEach((w) => w(tok)); waiters = []; };

async function refreshAccessToken() {
  const r = getRefreshToken();
  if (!r) return null;
  try {
    // explicit no-Authorization on refresh
    const { data } = await api.post(
      "/api/token/refresh/",
      { refresh: r },
      { headers: { Authorization: undefined } }
    );
    if (data?.access) setAuthToken(data.access);
    if (data?.refresh) setRefreshToken(data.refresh);
    return data?.access || null;
  } catch {
    return null;
  }
}

function logoutAndRedirect() {
  const next = window.location.pathname + window.location.search;
  clearAuth();
  if (!window.location.pathname.startsWith("/login")) {
    window.location.assign(`/login?next=${encodeURIComponent(next)}`);
  }
}

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const status = error?.response?.status;
    const original = error?.config || {};
    if ((status === 401 || status === 403) && !original._retry && !isPublicPath(original.url)) {
      original._retry = true;
      if (!getRefreshToken()) { logoutAndRedirect(); return Promise.reject(error); }

      if (!isRefreshing) {
        isRefreshing = true;
        const tok = await refreshAccessToken();
        isRefreshing = false;
        notify(tok);
        if (!tok) { logoutAndRedirect(); return Promise.reject(error); }
      }

      return new Promise((resolve, reject) => {
        waiters.push((tok) => {
          if (!tok) return reject(error);
          original.headers = { ...(original.headers || {}), Authorization: `Bearer ${tok}` };
          resolve(api(original));
        });
      });
    }
    return Promise.reject(error);
  }
);

/* ─────────────────────── Exports ────────────────────────────
   - default: authenticated client
   - publicApi: unauthenticated client for public endpoints
*/
export default api;
export { publicApi };